<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { getFormColor, FORM_TYPE_LABELS } from '../../../composables/useReservationsCalendar.js'

const props = defineProps({
    open:           { type: Boolean, default: false },
    forms:          { type: Array,   default: () => [] },
    visibleFormIds: { type: Set,     default: () => new Set() },
    reservations:   { type: Array,   default: () => [] },
    // { top, left } — pozice relativně k viewportu (od triggeru)
    anchorRect:     { type: Object,  default: null },
})

const emit = defineEmits(['close', 'toggle-form'])

// ─── Skupiny po typech ────────────────────────────────────────────

const typeOrder = ['personal', 'service', 'rental', 'accommodation']

const groups = computed(() => {
    const map = {}
    for (const form of props.forms) {
        const key = form.form_type?.name ?? 'personal'
        ;(map[key] ??= []).push(form)
    }
    return typeOrder
        .filter(k => map[k]?.length)
        .map(k => ({ key: k, label: FORM_TYPE_LABELS[k], forms: map[k] }))
})

function formCount(id) {
    return props.reservations.filter(r => r.id_reservation_forms === id).length
}

// ─── Pozice ───────────────────────────────────────────────────────

const popoverStyle = computed(() => {
    const left = props.anchorRect ? props.anchorRect.right + 8 : 268
    // Popover se zobrazí napravo od sidebaru, ukotvený výše (80px od vrchu viewportu)
    return {
        top:  '80px',
        left: `${left}px`,
    }
})
</script>

<template>
    <Teleport to="body">
        <!-- Scrim -->
        <Transition name="fp-scrim">
            <div
                v-if="open"
                class="fixed inset-0 z-[140]"
                @click="emit('close')"
            />
        </Transition>

        <!-- Popover -->
        <Transition name="fp">
            <div
                v-if="open"
                class="fixed z-[141] w-[440px] bg-white border border-neutral-200 rounded-2xl
                       shadow-[0_24px_60px_-16px_rgba(15,17,21,.25),0_4px_16px_rgba(15,17,21,.07)]
                       overflow-hidden"
                :style="popoverStyle"
            >
                <!-- Header -->
                <div class="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
                    <span class="text-sm font-semibold text-neutral-800">{{ $t('reservations.forms_picker_title') }}</span>
                    <div class="flex items-center gap-2">
                        <Link
                            href="/app/forms"
                            class="text-[11px] text-blue-500 font-medium hover:text-blue-700"
                            @click="emit('close')"
                        >
                            {{ $t('reservations.forms_manage') }}
                        </Link>
                        <button
                            class="w-7 h-7 rounded-full flex items-center justify-center text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                            @click="emit('close')"
                        >
                            <i class="pi pi-times !text-xs"/>
                        </button>
                    </div>
                </div>

                <!-- Skupiny -->
                <div class="px-3 py-2 max-h-[70vh] overflow-y-auto">
                    <div
                        v-for="group in groups"
                        :key="group.key"
                        class="mb-3"
                    >
                        <!-- Typ header -->
                        <div class="flex items-center gap-2 px-1 pt-1 pb-1.5">
                            <span class="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                                {{ group.label }}
                            </span>
                            <div class="flex-1 h-px bg-neutral-100"/>
                        </div>

                        <!-- 2-sloupcový grid formulářů -->
                        <div class="grid grid-cols-2 gap-0.5">
                            <button
                                v-for="form in group.forms"
                                :key="form.id"
                                class="flex items-center gap-2 px-2 py-2 rounded-lg text-left hover:bg-neutral-50 transition-colors group/item"
                                :class="visibleFormIds.has(form.id) ? '' : 'opacity-40'"
                                @click="emit('toggle-form', form.id)"
                            >
                                <!-- Eye -->
                                <i
                                    class="!text-[11px] flex-shrink-0 w-3.5 transition-colors"
                                    :class="visibleFormIds.has(form.id) ? 'pi pi-eye' : 'pi pi-eye-slash text-neutral-300'"
                                    :style="visibleFormIds.has(form.id) ? { color: getFormColor(form) } : {}"
                                />
                                <!-- Barva -->
                                <div
                                    class="w-2 h-2 rounded-[3px] flex-shrink-0"
                                    :style="{ background: getFormColor(form) }"
                                />
                                <!-- Název -->
                                <span class="flex-1 text-[12px] font-medium text-neutral-800 truncate min-w-0 leading-tight">
                                    {{ form.name }}
                                </span>
                                <!-- Počet -->
                                <span class="text-[11px] text-neutral-400 tabular-nums flex-shrink-0">
                                    {{ formCount(form.id) }}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.fp-scrim-enter-active, .fp-scrim-leave-active { transition: opacity .1s; }
.fp-scrim-enter-from, .fp-scrim-leave-to       { opacity: 0; }

.fp-enter-active { transition: opacity .15s ease, transform .15s cubic-bezier(.25,1,.3,1); }
.fp-leave-active { transition: opacity .1s ease; }
.fp-enter-from   { opacity: 0; transform: translateX(-8px) scale(.97); }
.fp-leave-to     { opacity: 0; }
</style>
